import { axiosClient } from './axiosClient';
import { APIUrls } from '../constant/constant';

const { OUTAGE_ENDPOINTS } = APIUrls;

/**
 * Get all sites with their locations
 * @returns {Promise<Object>} Sites data with locations
 */
export async function getSitesAndLocations() {
  try {
    const response = await axiosClient.get('/devices/sites');
    return response.data;
  } catch (error) {
    console.error('Error fetching sites and locations:', error);
    throw error;
  }
}

/**
 * Get devices based on store_id and location_id filters
 * @param {string} storeId - Store ID filter (optional, use 'ALL' for all stores)
 * @param {string} locationId - Location ID filter (optional, use 'ALL' for all locations)
 * @returns {Promise<Object>} Devices data
 */
export async function getDevices(storeId = 'ALL', locationId = 'ALL') {
  try {
    const params = {};
    if (storeId && storeId !== 'ALL' && storeId !== 'all') {
      params.store_id = storeId;
    }
    if (locationId && locationId !== 'ALL' && locationId !== 'all') {
      params.location_id = locationId;
    }

    const response = await axiosClient.get('/devices', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
}

/**
 * Get outage devices with date range and optional filters
 * @param {Object} params - Query parameters
 * @param {boolean} params.allsites - Whether to get all sites (for initial load)
 * @param {string} params.start_date - Start date (YYYY-MM-DD format)
 * @param {string} params.end_date - End date (YYYY-MM-DD format)
 * @param {string} params.store_id - Store ID filter (optional)
 * @param {string} params.location_id - Location ID filter (optional)
 * @returns {Promise<Object>} Outage devices data
 */
export async function getOutageDevices(params = {}) {
  try {
    const queryParams = {};
    
    // Add required parameters
    if (params.allsites !== undefined) {
      queryParams.allsites = params.allsites;
    }
    if (params.start_date) {
      queryParams.start_date = params.start_date;
    }
    if (params.end_date) {
      queryParams.end_date = params.end_date;
    }
    
    // Add optional filters
    if (params.store_id && params.store_id !== 'all') {
      queryParams.store_id = params.store_id;
    }
    if (params.location_id && params.location_id !== 'all') {
      queryParams.location_id = params.location_id;
    }

    const response = await axiosClient.get(OUTAGE_ENDPOINTS.GET_OUTAGE_DEVICES, { params: queryParams });
    return response.data;
  } catch (error) {
    console.error('Error fetching outage devices:', error);
    throw error;
  }
}

/**
 * Get offline events for a specific device
 * @param {string} deviceId - Device ID or plug ID
 * @param {Object} params - Query parameters (optional)
 * @param {string} params.start_date - Start date (YYYY-MM-DD format)
 * @param {string} params.end_date - End date (YYYY-MM-DD format)
 * @returns {Promise<Object>} Offline events data with offline_time, online_time, status
 */
export async function getOfflineEvents(deviceId, queryParams = {}) {
  try {
    const endpoint = `${OUTAGE_ENDPOINTS.GET_OFFLINE_EVENTS}/${deviceId}`;
    const response = await axiosClient.get(endpoint, { params: queryParams });
    return response.data;
  } catch (error) {
    console.error('Error fetching offline events:', error);
    throw error;
  }
}

/**
 * Get outage count for specified parameters
 * @param {Object} params - Query parameters
 * @param {boolean} params.allsites - Whether to get all sites (for initial load)
 * @param {string} params.start_date - Start date (YYYY-MM-DD format)
 * @param {string} params.end_date - End date (YYYY-MM-DD format)
 * @param {string} params.store_id - Store ID filter (optional)
 * @param {string} params.location_id - Location ID filter (optional)
 * @returns {Promise<Object>} Outage count data
 */
export async function getOutageCount(params = {}) {
  try {
    const queryParams = {};
    
    // Add required parameters
    if (params.allsites !== undefined) {
      queryParams.allsites = params.allsites;
    }
    if (params.start_date) {
      queryParams.start_date = params.start_date;
    }
    if (params.end_date) {
      queryParams.end_date = params.end_date;
    }
    
    // Add optional filters
    if (params.store_id && params.store_id !== 'all') {
      queryParams.store_id = params.store_id;
    }
    if (params.location_id && params.location_id !== 'all') {
      queryParams.location_id = params.location_id;
    }

    const response = await axiosClient.get(OUTAGE_ENDPOINTS.GET_OUTAGE_COUNT, { params: queryParams });
    return response.data;
  } catch (error) {
    console.error('Error fetching outage count:', error);
    // throw error;
    return 7;
  }
}

/**
 * Export devices report with same parameters as getOutageDevices
 * @param {Object} params - Query parameters (same as getOutageDevices)
 * @param {boolean} params.allsites - Whether to get all sites (for initial load)
 * @param {string} params.start_date - Start date (YYYY-MM-DD format)
 * @param {string} params.end_date - End date (YYYY-MM-DD format)
 * @param {string} params.store_id - Store ID filter (optional)
 * @param {string} params.location_id - Location ID filter (optional)
 * @returns {Promise<Object>} Devices report data for export
 */
export async function exportDevicesReport(params = {}) {
  try {
    const queryParams = {};
    
    // Add required parameters
    if (params.allsites !== undefined) {
      queryParams.allsites = params.allsites;
    }
    if (params.start_date) {
      queryParams.start_date = params.start_date;
    }
    if (params.end_date) {
      queryParams.end_date = params.end_date;
    }
    
    // Add optional filters
    if (params.store_id && params.store_id !== 'all') {
      queryParams.store_id = params.store_id;
    }
    if (params.location_id && params.location_id !== 'all') {
      queryParams.location_id = params.location_id;
    }

    const response = await axiosClient.get(OUTAGE_ENDPOINTS.EXPORT_DEVICES_REPORT, { params: queryParams });
    return response.data;
  } catch (error) {
    console.error('Error exporting devices report:', error);
    throw error;
  }
}
